import React from "react";
import styled from "styled-components";

type LegendType =
  | "rect"
  | "circle"
  | "line"
  | "line-circle"
  | "hatched"
  | "custom";

interface LegendItem {
  color: string;
  label: string;
  type: LegendType;
  custom?: React.ReactNode;
}

interface ColorKeyProps {
  data?: LegendItem[][];
}

interface ColorProps {
  color: string;
}

const ItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  background-color: var(--site-background);
`;

const Label = styled.span`
  font-size: 13px;
`;

const SymbolBase = styled.div`
  width: 14px;
  height: 14px;
`;

const Rectangle = styled(SymbolBase)<ColorProps>`
  background-color: ${(props) => props.color};
`;

const Circle = styled(SymbolBase)<ColorProps>`
  background-color: ${(props) => props.color};
  border-radius: 50%;
`;

const LineContainer = styled(SymbolBase)`
  display: flex;
  align-items: center;
`;

const Line = styled.div<ColorProps>`
  width: 100%;
  height: 4px;
  background-color: ${(props) => props.color};
  transform: rotate(-45deg);
`;

const LineWithCircleContainer = styled(LineContainer)`
  position: relative;
`;

const CircleOverlay = styled.div<ColorProps>`
  width: 8px;
  height: 8px;
  background-color: ${(props) => props.color};
  border-radius: 50%;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const HatchedBox = styled(SymbolBase)<ColorProps>`
  background-image: ${(props) => `repeating-linear-gradient(
    45deg,
    ${props.color},
    ${props.color} 2px,
    transparent 2px,
    transparent 6px
  )`};
`;

const LegendItem: React.FC<{ item: LegendItem }> = ({ item }) => {
  const { color, label, type } = item;

  const renderSymbol = () => {
    switch (type) {
      case "rect":
        return <Rectangle color={color} />;

      case "circle":
        return <Circle color={color} />;

      case "line":
        return (
          <LineContainer>
            <Line color={color} />
          </LineContainer>
        );

      case "line-circle":
        return (
          <LineWithCircleContainer>
            <Line color={color} />
            <CircleOverlay color={color} />
          </LineWithCircleContainer>
        );

      case "hatched":
        return <HatchedBox color={color} />;

      case "custom":
        return item.custom;

      default:
        return null;
    }
  };

  return (
    <ItemContainer>
      {renderSymbol()}
      <Label>{label}</Label>
    </ItemContainer>
  );
};

const ColorKey: React.FC<ColorKeyProps> = ({ data = [] }) => {
  if (!Array.isArray(data)) {
    return null;
  }

  return (
    <Container>
      {data.map((row, index) => (
        <Row key={index}>
          {row.map((item, index) => (
            <LegendItem key={index} item={item} />
          ))}
        </Row>
      ))}
    </Container>
  );
};

const Container = styled.div`
  line-height: 1em;
  margin-bottom: 12px;
`;

const Row = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 14px;
  row-gap: 0px;
  background-color: var(--site-background);
  line-height:0em;
`;
export default ColorKey;
